import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { Flex } from 'components/shared/flex';
import ThemedButton from 'components/shared/themed_button';
import React from 'react';
import { withStyles } from 'tss-react/mui';
import { ProofreadPlanFilterSet } from 'utils/hooks/use_proofread_plan_instrument_filter';

const Accordion = withStyles(MuiAccordion, {
  root: {
    boxShadow: 'none',
  },
});

type ProofreadPlanInstrumentFilterAccordionProps = {
  filter: ProofreadPlanFilterSet;
};

export const ProofreadPlanInstrumentFilterAccordion: React.FC<ProofreadPlanInstrumentFilterAccordionProps> = ({
  filter,
}) => {
  if (filter.proofreadDropdownExColumns.length === 0) return null;

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: '#f9f9f9' }}>
        <Typography variant="subtitle1">校正管理情報で機器を絞り込む</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box style={{ width: '100%' }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            機器に設定された校正管理情報が一致する機器のみ登録されます。
            <br />
            絞り込み条件に選択した校正管理情報は計画内の機器の管理情報に反映されます。
          </Typography>
          {filter.filterConditions.map((condition, conditionIndex) => (
            <React.Fragment key={conditionIndex}>
              {conditionIndex > 0 && (
                <Divider style={{ margin: '16px 0' }}>
                  <Typography variant="body2" color="textSecondary">
                    または
                  </Typography>
                </Divider>
              )}
              <Paper variant="outlined" style={{ padding: 12, marginBottom: 8 }}>
                <Flex justifyContent="space-between" alignItems="center" style={{ marginBottom: 8 }}>
                  <Typography variant="body2">絞り込み条件 {conditionIndex + 1}</Typography>
                  {filter.filterConditions.length > 1 && (
                    <IconButton size="small" onClick={() => filter.handleRemoveCondition(conditionIndex)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Flex>
                {condition.map((row, filterIndex) => {
                  const usedExColumnIds = condition
                    .filter((_, i) => i !== filterIndex)
                    .map((f) => f.exColumnId)
                    .filter((id) => id !== '');
                  const availableExColumns = filter.proofreadDropdownExColumns.filter(
                    (col) => !usedExColumnIds.includes(String(col.id))
                  );
                  const selectedExColumn = filter.proofreadDropdownExColumns.find(
                    (col) => String(col.id) === row.exColumnId
                  );

                  return (
                    <Flex key={filterIndex} alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                      <FormControl variant="outlined" size="small" style={{ minWidth: 140, flex: 1 }}>
                        <InputLabel id={`ex-col-label-${conditionIndex}-${filterIndex}`}>項目</InputLabel>
                        <Select
                          labelId={`ex-col-label-${conditionIndex}-${filterIndex}`}
                          label="項目"
                          value={row.exColumnId}
                          onChange={(e: SelectChangeEvent) =>
                            filter.handleExColumnChange(conditionIndex, filterIndex, e.target.value)
                          }
                        >
                          {availableExColumns.map((col) => (
                            <MenuItem key={col.id} value={String(col.id)}>
                              {col.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 140, flex: 1 }}
                        disabled={!row.exColumnId}
                      >
                        <InputLabel id={`val-label-${conditionIndex}-${filterIndex}`}>選択肢</InputLabel>
                        <Select
                          labelId={`val-label-${conditionIndex}-${filterIndex}`}
                          label="選択肢"
                          value={row.value}
                          onChange={(e: SelectChangeEvent) =>
                            filter.handleValueChange(conditionIndex, filterIndex, e.target.value)
                          }
                        >
                          {(selectedExColumn?.options || []).map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton size="small" onClick={() => filter.handleRemoveFilter(conditionIndex, filterIndex)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Flex>
                  );
                })}
                <ThemedButton
                  onClick={() => filter.handleAddFilter(conditionIndex)}
                  color="primary"
                  disabled={condition.length >= filter.proofreadDropdownExColumns.length}
                >
                  + 項目を追加
                </ThemedButton>
              </Paper>
            </React.Fragment>
          ))}
          <Box style={{ marginTop: 8 }}>
            <ThemedButton onClick={filter.handleAddCondition} color="primary">
              + 絞り込み条件を追加
            </ThemedButton>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
